import React from 'react';
import { Link } from 'react-router-dom';
import {
  HardHat,
  Wrench,
  Zap,
  Layers,
  Paintbrush,
  LayoutGrid,
  Wind,
  TreePine,
  Utensils,
  Droplet,
  Sun,
  Square,
  DoorOpen,
  Hammer,
  Ruler,
  Building2,
  ArrowRight,
} from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { BRAND, TRADES, tradeLabel, tradeAccent } from '@/data/constants';


/** Trade slug -> icon. Keys mirror TRADES in constants.ts. */
const TRADE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  roofing: HardHat,
  plumbing: Wrench,
  electrical: Zap,
  hvac: Wind,
  flooring: LayoutGrid,
  painting: Paintbrush,
  solar: Sun,
  concrete: Layers,
  landscaping: TreePine,
  windows: Square,
  doors: DoorOpen,
  'kitchen-remodel': Utensils,
  'bathroom-remodel': Droplet,
  'general-construction': Building2,
  framing: Hammer,
  drywall: Ruler,
};

/**
 * Horizontal, swipeable category rail. Renders every trade from the shared
 * TRADES list so a new trade automatically appears here, in the filters and in
 * the SEO hubs without a second edit.
 */
const CategoryStrip: React.FC = () => {
  const { t, path, lang } = useI18n();

  return (
    <section className="bg-white py-10 sm:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">
              {t('home.categoryTitle')}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{t('home.categorySub')}</p>
          </div>
          <Link
            to={path('/jobs')}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-orange transition-colors hover:text-orange-700"
          >
            {t('common.viewAll')} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Edge-to-edge scroller on mobile, contained on desktop. */}
      <div className="mt-6 overflow-x-auto pb-3">
        <ul className="mx-auto flex w-max gap-3 px-4 sm:px-6 lg:px-8">
          {TRADES.map((tr) => {
            const Icon = TRADE_ICONS[tr.slug] || HardHat;
            const accent = tradeAccent(tr.slug);
            return (
              <li key={tr.slug}>
                <Link
                  to={`${path('/jobs')}?trade=${tr.slug}`}
                  className="group flex w-[132px] flex-col items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-3 py-5 text-center shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-slate-300 hover:shadow-card sm:w-[148px]"
                >
                  <span
                    className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ring-1 transition-transform duration-200 group-hover:scale-110 ${accent.bg} ${accent.fg} ${accent.ring}`}
                  >
                    <Icon className="h-6 w-6" />
                  </span>
                  <span className="text-sm font-bold leading-snug text-slate-800 group-hover:text-slate-900">
                    {tradeLabel(tr.slug, lang)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {/* The only place trades are listed on the home page, so the "trade not
          listed" escape hatch that used to live in the deleted photo grid
          section moves here rather than being lost. */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="mt-2 text-sm text-slate-500">
          {t('home.tradesContact')}{' '}
          <a href={`mailto:${BRAND.email}`} className="font-semibold text-brand-orange hover:text-orange-700">
            {BRAND.email}
          </a>
        </p>
      </div>
    </section>

  );
};

export default CategoryStrip;
